import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { fetchUserInfo, type UserInfoResponse } from '../services/usersService';
import { useAuth } from '../AuthProvider';
import { getAccessToken } from '../auth/tokenManager';
import { logUsage } from '../services/usageService';
import { useDemoUser } from '../hooks/useDemoUser';
import { usePlatformSessionTelemetry } from '../hooks/usePlatformSessionTelemetry';

export type Permission = string;

interface SecurityState {
  loading: boolean;
  user: UserInfoResponse['user'] | null;
  roles: UserInfoResponse['roles'];
  permissions: Set<Permission>;
  has: (perm: Permission) => boolean;
  refresh: () => Promise<void>;
}

const SecurityContext = createContext<SecurityState | undefined>(undefined);

export const SecurityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useState<UserInfoResponse['user'] | null>(null);
  const [roles, setRoles] = useState<UserInfoResponse['roles']>([]);
  const [permissions, setPermissions] = useState<Set<Permission>>(new Set());
  const { accounts } = useAuth();
  const { demoUserEmail, clearDemoUserEmail } = useDemoUser();
  const inFlightLoad = useRef<Promise<void> | null>(null);
  const didLogUsage = useRef(false);
  const accountKey = accounts && accounts.length > 0 ? accounts[0].homeAccountId : undefined;
  usePlatformSessionTelemetry(user);

  const load = useCallback(async () => {
    if (!accountKey) return;
    if (inFlightLoad.current) {
      await inFlightLoad.current;
      return;
    }

    const request = (async () => {
      try {
        setLoading(true);
        const accessToken = await getAccessToken();
        if (!accessToken) {
          setUser(null);
          setRoles([]);
          setPermissions(new Set());
          return;
        }
        const data = await fetchUserInfo(accessToken);
        setUser(data.user || null);
        setRoles(Array.isArray(data.roles) ? data.roles : []);
        setPermissions(new Set(Array.isArray(data.permissions) ? data.permissions : []));

        const resolvedEmail = data.user?.emailAddress?.trim().toLowerCase() || '';
        if (demoUserEmail && resolvedEmail && resolvedEmail !== demoUserEmail) {
          clearDemoUserEmail();
        }

        // Log usage once per browser session
        if (!didLogUsage.current) {
          didLogUsage.current = true;
          logUsage();
        }
      } catch (_e) {
        setUser(null);
        setRoles([]);
        setPermissions(new Set());
      } finally {
        setLoading(false);
        inFlightLoad.current = null;
      }
    })();

    inFlightLoad.current = request;
    await request;
  }, [accountKey, clearDemoUserEmail, demoUserEmail]);

  useEffect(() => {
    load();
  }, [accountKey, demoUserEmail, load]);

  const value: SecurityState = useMemo(() => ({
    loading,
    user,
    roles,
    permissions,
    has: (perm: Permission) => permissions.has(perm),
    refresh: load,
  }), [loading, user, roles, permissions, load]);

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};

export function useSecurity() {
  const ctx = useContext(SecurityContext);
  if (!ctx) throw new Error('useSecurity must be used within a SecurityProvider');
  return ctx;
}

export function IfAllowed({ permission, children }: { permission: Permission; children: React.ReactNode }) {
  const { has } = useSecurity();
  if (!has(permission)) return null;
  return <>{children}</>;
}


